import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Button } from 'antd-mobile';
import { useMemoizedFn } from 'ahooks';
import { useLocation } from 'react-router-dom';
import { Header, HeaderMemberInfo, ScrollView } from '@/components';
import { ALL_PAGES } from '@/constant';
import service from '@/service';
import useNav from '@/hooks/useNav';
import { selectedCurrentCustomer } from '@/store/customer';
import { selectCurrentServingOrder } from '@/store/service';
import { ScrollViewRefAttrs } from '@/components/scroll-view';
import PictureContent, { PictureContentRefAttrs } from '../action-test/picture-content';
import cx from './index.module.scss';

interface Props {}

const ActionRetest: React.FC<Props> = () => {
  const nav = useNav();
  const location = useLocation();
  const orderIdRef = useRef<string>(null);
  const [actionTestImgs, setActionTestImgs] = useState([]);
  const [beforeHandData, setBeforeHandData] = useState([]);
  const [afterWardsData, setAfterWardsData] = useState([]);
  const [isConfirming, setIsConfirming] = useState(false);
  const hasClickedSettle = useRef(false);
  const [questionnaire, setQuestionnaire] = useState<ActionTest.Questionnaire>({} as any);
  const questionnaireIdRef = useRef(null);
  const currentCustomer = useSelector(selectedCurrentCustomer);
  const currentServingOrder = useSelector(selectCurrentServingOrder);
  const scrollViewRef = useRef<ScrollViewRefAttrs>(null);
  const pictureContentRef = useRef<PictureContentRefAttrs>(null);
  const [isScrollTop, setIsScrollTop] = useState<boolean>(true);

  const onScrolling = useMemoizedFn(pos => {
    setIsScrollTop(pos.y > 0);
  });

  const handleScrollElement = () => {
    const maxScrollY = scrollViewRef.current?.bsInstance?.maxScrollY;
    setIsScrollTop(false);
    scrollViewRef.current?.bsInstance?.scrollTo(0, maxScrollY, 200);
  };
  const getActionTestQuestionnaire = useCallback(
    async serviceOrderId => {
      if (!serviceOrderId) {
        nav(ALL_PAGES.MEMBER_DETAIL);
        return;
      }
      const { data } = await service.actionTest.getQuestionnaireAndAnswer({
        step: 'ActionRetest',
        serviceOrderId,
      });
      scrollViewRef.current?.handleRefresh();
      questionnaireIdRef.current = data?.questionnaireId;
      const parsedQuestionnaire = JSON.parse(data.questionnaire) as ActionTest.Questionnaire;
      setQuestionnaire(parsedQuestionnaire);
      setBeforeHandData(data?.beforehand?.map(item => (typeof item === 'string' ? JSON.parse(item) : item)));
      setAfterWardsData(data?.afterwards?.map(item => (typeof item === 'string' ? JSON.parse(item) : item)));
    },
    [nav]
  );

  // 确认并结算订单
  const handleConfirm = useCallback(async () => {
    if (hasClickedSettle.current) return;
    hasClickedSettle.current = true;

    try {
      setIsConfirming(true);
      const formatactionTestImgs = actionTestImgs?.map(item =>
        typeof item === 'string' ? item : JSON.stringify(item)
      );

      await pictureContentRef.current?.savePainLevel(
        orderIdRef.current,
        questionnaireIdRef.current,
        formatactionTestImgs
      );

      setTimeout(
        () =>
          nav(location.state?.isAllProcessSuccess ? ALL_PAGES.HOME : ALL_PAGES.MEMBER_DETAIL, {
            state: {
              orderId: orderIdRef.current,
              isNeedShowDrawerAfterFinish: location?.state?.isNeedShowDrawerAfterFinish,
              customerId: currentServingOrder?.customerId,
            },
          }),
        200
      );
    } catch (e) {
      console.log(e);
      hasClickedSettle.current = false;
    } finally {
      setIsConfirming(false);
    }
  }, [
    actionTestImgs,
    currentServingOrder?.customerId,
    location.state?.isAllProcessSuccess,
    location.state?.isNeedShowDrawerAfterFinish,
    nav,
  ]);

  useEffect(() => {
    orderIdRef.current = location.state?.orderId;
    getActionTestQuestionnaire(orderIdRef.current);
    pictureContentRef.current?.getPainLevelAfter(orderIdRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className={cx('action-retest')}>
      <Header
        title={
          <HeaderMemberInfo
            name={currentCustomer?.name ?? currentServingOrder?.customerName}
            handleClick={() => {}}
            avatarUrl={currentCustomer?.avatarUrl ?? currentServingOrder?.userAvatar}
          />
        }
        backConfig={{ text: '会员详情', action: () => nav(ALL_PAGES.MEMBER_DETAIL) }}
      />
      <ScrollView
        ref={scrollViewRef}
        realTime
        className={cx('content-box', { scrolling: !isScrollTop })}
        onScrolling={onScrolling}
      >
        <div className={cx('content-inner-box')}>
          <PictureContent
            ref={pictureContentRef}
            setActionTestImgs={setActionTestImgs}
            currentServingOrder={currentServingOrder}
            beforeHandData={beforeHandData}
            afterWardsData={afterWardsData}
            questionnaire={questionnaire}
            afterMassage
            handleScrollElement={handleScrollElement}
          />
        </div>
      </ScrollView>
      <div className={cx('footer')}>
        <Button className={cx('btn', 'btn-primary')} loading={isConfirming} onClick={handleConfirm}>
          确认并结束
        </Button>
      </div>
    </div>
  );
};

export default ActionRetest;
